import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/toast';
import {
  DollarSign, Link, Copy, Users, TrendingUp, Gift,
  ArrowRight, Plus, ExternalLink, Check, Clock, XCircle, CheckCircle2,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface Referral {
  id: string;
  code: string;
  description: string | null;
  clicks: number;
  signups: number;
  conversions: number;
  commission_rate: string;
  is_active: boolean;
  total_referred: number;
  total_converted: number;
  total_earned_cents: number;
  created_at: string;
}

interface Commission {
  id: string;
  referral_id: string;
  referral_code: string;
  referrer_id: string;
  referred_user_id: string;
  amount_cents: number;
  currency: string;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  metadata: string | null;
  created_at: string;
}

interface CommissionSummary {
  total_earned_cents: number;
  pending_cents: number;
  paid_cents: number;
  total_commissions: number;
  total_clicks: number;
  total_signups: number;
  total_conversions: number;
  conversion_rate: number;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export function Affiliates() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  /* ---- Queries ---- */

  const { data: summary, isLoading: summaryLoading } = useQuery<CommissionSummary>({
    queryKey: ['affiliates', 'summary'],
    queryFn: async () => {
      const { data } = await api.get('/affiliates/commissions/summary');
      return data;
    },
  });

  const { data: referrals, isLoading: referralsLoading } = useQuery<Referral[]>({
    queryKey: ['affiliates', 'referrals'],
    queryFn: async () => {
      const { data } = await api.get('/affiliates/referrals');
      return data;
    },
  });

  const { data: commissionsData, isLoading: commissionsLoading } = useQuery<{
    data: Commission[];
    meta: { page: number; limit: number; total: number; totalPages: number };
  }>({
    queryKey: ['affiliates', 'commissions'],
    queryFn: async () => {
      const { data } = await api.get('/affiliates/commissions?page=1&limit=20');
      return data;
    },
  });

  /* ---- Mutations ---- */

  const createReferral = useMutation({
    mutationFn: async (description: string) => {
      const { data } = await api.post('/affiliates/referrals', {
        description: description || undefined,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliates'] });
      setCreateDialogOpen(false);
      setNewDescription('');
      showSuccess(t('affiliates.createCodeSuccess'));
    },
    onError: () => {
      showError(t('affiliates.createCodeError'));
    },
  });

  /* ---- Helpers ---- */

  const formatCents = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const baseUrl = window.location.origin;
  const referralLink = referrals && referrals.length > 0
    ? `${baseUrl}/ref/${referrals[0].code}`
    : `${baseUrl}/ref/YOUR_CODE`;

  const copyToClipboard = async (text: string, type: 'code' | 'link', code?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'code' && code) {
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
      } else {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      }
      showSuccess(t('affiliates.copiedToClipboard'));
    } catch {
      showError(t('affiliates.copyError'));
    }
  };

  const getStatusBadge = (status: Commission['status']) => {
    const styles: Record<Commission['status'], { className: string; icon: React.ReactNode; label: string }> = {
      pending: {
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
        icon: <Clock className="h-3 w-3" />,
        label: t('affiliates.pending'),
      },
      approved: {
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
        icon: <CheckCircle2 className="h-3 w-3" />,
        label: t('affiliates.approved'),
      },
      paid: {
        className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
        icon: <Check className="h-3 w-3" />,
        label: t('affiliates.paid'),
      },
      rejected: {
        className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
        icon: <XCircle className="h-3 w-3" />,
        label: t('affiliates.rejected'),
      },
    };
    const s = styles[status];
    return (
      <Badge variant="outline" className={cn('gap-1 font-medium', s.className)}>
        {s.icon}
        {s.label}
      </Badge>
    );
  };

  const commissions = commissionsData?.data || [];

  /* ---- Stats cards config ---- */

  const statsCards = [
    {
      title: t('affiliates.totalEarnings'),
      value: formatCents(summary?.total_earned_cents || 0),
      icon: DollarSign,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
    },
    {
      title: t('affiliates.pendingCommissions'),
      value: formatCents(summary?.pending_cents || 0),
      icon: Clock,
      color: 'text-yellow-500',
      bg: 'bg-yellow-500/10',
    },
    {
      title: t('affiliates.activeReferrals'),
      value: summary?.total_signups?.toString() || '0',
      icon: Users,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
    },
    {
      title: t('affiliates.conversionRate'),
      value: `${summary?.conversion_rate || 0}%`,
      icon: TrendingUp,
      color: 'text-purple-500',
      bg: 'bg-purple-500/10',
    },
  ];

  /* ---- How It Works steps ---- */

  const howItWorksSteps = [
    {
      icon: Link,
      title: t('affiliates.step1Title'),
      description: t('affiliates.step1Desc'),
    },
    {
      icon: Users,
      title: t('affiliates.step2Title'),
      description: t('affiliates.step2Desc'),
    },
    {
      icon: TrendingUp,
      title: t('affiliates.step3Title'),
      description: t('affiliates.step3Desc'),
    },
    {
      icon: DollarSign,
      title: t('affiliates.step4Title'),
      description: t('affiliates.step4Desc'),
    },
  ];

  /* ---------------------------------------------------------------- */
  /*  Render                                                          */
  /* ---------------------------------------------------------------- */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Gift className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {t('affiliates.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('affiliates.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {summaryLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards.map((stat) => (
            <Card key={stat.title} className="transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/5 hover:border-emerald-200 dark:hover:border-emerald-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center', stat.bg)}>
                    <stat.icon className={cn('h-5 w-5', stat.color)} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Separator />

      {/* Referral Link Section */}
      <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Link className="h-4 w-4 text-emerald-500" />
            {t('affiliates.referralLink')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={referralLink}
              className="bg-white dark:bg-background font-mono text-sm"
            />
            <Button
              variant="outline"
              size="icon"
              className={cn(
                'shrink-0 transition-all',
                copiedLink
                  ? 'border-emerald-500 text-emerald-600'
                  : 'hover:border-emerald-300 hover:text-emerald-600',
              )}
              onClick={() => copyToClipboard(referralLink, 'link')}
            >
              {copiedLink ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="shrink-0 hover:border-emerald-300 hover:text-emerald-600"
              onClick={() => window.open(referralLink, '_blank')}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {t('affiliates.referralLinkDescription')}
          </p>
        </CardContent>
      </Card>

      {/* Referral Codes Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {t('affiliates.referralCodes')}
            </CardTitle>
            <Button
              size="sm"
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="h-4 w-4" />
              {t('affiliates.createCode')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {referralsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : !referrals || referrals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-4">
                <Link className="h-7 w-7 text-muted-foreground/40" />
              </div>
              <h3 className="text-base font-medium mb-1">
                {t('affiliates.noReferrals')}
              </h3>
              <p className="text-sm text-muted-foreground text-center max-w-sm mb-4">
                {t('affiliates.noReferralsDescription')}
              </p>
              <Button
                size="sm"
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4" />
                {t('affiliates.createCode')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {referrals.map((ref) => (
                <div
                  key={ref.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border bg-card transition-all hover:shadow-sm"
                >
                  {/* Code + description */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-sm font-bold font-mono bg-muted px-2 py-0.5 rounded">
                        {ref.code}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          'h-6 w-6 shrink-0',
                          copiedCode === ref.code
                            ? 'text-emerald-600'
                            : 'text-muted-foreground hover:text-emerald-600',
                        )}
                        onClick={() => copyToClipboard(ref.code, 'code', ref.code)}
                      >
                        {copiedCode === ref.code ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[11px]',
                          ref.is_active
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                            : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-700',
                        )}
                      >
                        {ref.is_active
                          ? t('affiliates.active')
                          : t('affiliates.inactive')}
                      </Badge>
                    </div>
                    {ref.description && (
                      <p className="text-xs text-muted-foreground truncate">
                        {ref.description}
                      </p>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="text-center">
                      <p className="font-semibold text-foreground">{ref.clicks}</p>
                      <p className="text-[11px]">{t('affiliates.clicks')}</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-foreground">{ref.signups}</p>
                      <p className="text-[11px]">{t('affiliates.signups')}</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-foreground">{ref.conversions}</p>
                      <p className="text-[11px]">{t('affiliates.conversions')}</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-foreground">
                        {(parseFloat(ref.commission_rate) * 100).toFixed(0)}%
                      </p>
                      <p className="text-[11px]">{t('affiliates.rate')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commissions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('affiliates.commissionHistory')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {commissionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : commissions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-4">
                <DollarSign className="h-7 w-7 text-muted-foreground/40" />
              </div>
              <h3 className="text-base font-medium mb-1">
                {t('affiliates.noCommissions')}
              </h3>
              <p className="text-sm text-muted-foreground text-center max-w-sm">
                {t('affiliates.noCommissionsDescription')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('affiliates.amount')}
                    </th>
                    <th className="pb-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('affiliates.status')}
                    </th>
                    <th className="pb-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('affiliates.referralCode')}
                    </th>
                    <th className="pb-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('affiliates.currency')}
                    </th>
                    <th className="pb-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('affiliates.date')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {commissions.map((commission) => (
                    <tr key={commission.id} className="group hover:bg-muted/50 transition-colors">
                      <td className="py-3 pr-4">
                        <span className="font-semibold">
                          {formatCents(commission.amount_cents)}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        {getStatusBadge(commission.status)}
                      </td>
                      <td className="py-3 pr-4">
                        <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                          {commission.referral_code}
                        </code>
                      </td>
                      <td className="py-3 pr-4 text-sm text-muted-foreground">
                        {commission.currency}
                      </td>
                      <td className="py-3 text-sm text-muted-foreground">
                        {new Date(commission.created_at).toLocaleDateString(
                          i18n.language === 'ja' ? 'ja-JP' : 'en-US',
                          { year: 'numeric', month: 'short', day: 'numeric' },
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* How It Works */}
      <div>
        <h2 className="text-lg font-bold mb-4">
          {t('affiliates.howItWorks')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {howItWorksSteps.map((step, index) => (
            <Card
              key={step.title}
              className="relative transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/5 hover:border-emerald-200 dark:hover:border-emerald-800"
            >
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <step.icon className="h-5 w-5 text-emerald-500" />
                  </div>
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">
                    {t('affiliates.stepLabel', { number: index + 1 })}
                  </span>
                </div>
                <h3 className="font-semibold text-sm mb-1">{step.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </CardContent>
              {/* Connector arrow between steps (hidden on last) */}
              {index < howItWorksSteps.length - 1 && (
                <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                  <ArrowRight className="h-5 w-5 text-emerald-400/60" />
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* Create Referral Code Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('affiliates.createCode')}
            </DialogTitle>
            <DialogDescription>
              {t('affiliates.createCodeDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                {t('affiliates.descriptionOptional')}
              </label>
              <Input
                placeholder={t('affiliates.descriptionPlaceholder')}
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                maxLength={255}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateDialogOpen(false);
                  setNewDescription('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
                onClick={() => createReferral.mutate(newDescription)}
                disabled={createReferral.isPending}
              >
                {createReferral.isPending ? (
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {t('common.create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
